import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box } from "@chakra-ui/react";
import { VelaWorkspace } from "@luxalgo/vela/workspace";
import type { OHLCV } from "@luxalgo/vela";
import type { TesseraLaneBOverlayId } from "../../../../../types/executionCandlesOverlays";
import type {
  ExecutionCandle,
  ExecutionCandlesRegimeOverlay,
  ExecutionTradeOverlay,
} from "../../../../../types/trade";
import type { ExecutionCandlesOverlaysBundle } from "../../../../../types/executionCandlesOverlays";
import { normalizeOverlaysBundle, zipBarTimesMs } from "../../../../../utils/executionCandlesOverlayZip";
import { mergeComputedStrategyRdOverlays } from "../../../../../utils/strategyRdConceptIndicators";
import type { StrategyRdChartChip } from "../../../../../utils/strategyRdChartChips";
import {
  ensureTesseraVelaFullPageAction,
  TESSERA_VELA_FULL_PAGE_EVENT,
} from "../../../../../utils/vela/tesseraVelaFullPage";

// Toolbar actions must exist before any Vela widget is constructed.
ensureTesseraVelaFullPageAction();
// Renderers pick up layers at mount: the selected trade's entry / exit price tags.
ensureTesseraAxisTagsLayer();
import {
  strategyRdChartSeriesFromResponse,
  strategyRdLanesCoveredBySeries,
  strategyRdRetargetPaneId,
} from "../../../../../utils/strategyRdIndicatorSeries";
import type { StrategyRdOscComputeParams } from "../../../../../utils/strategyRdConceptIndicators";
import { strategyRdConceptVisibleRange, strategyRdRecentVisibleRange } from "../../../../../utils/strategyRdConceptViewport";
import {
  ensureTesseraAxisTagsLayer,
  TESSERA_AXIS_TAGS_LAYER,
  type TesseraAxisTag,
} from "../../../../../utils/vela/tesseraAxisTags";
import { tradeAxisLevels } from "../../../../../utils/vela/tesseraTradeMarkerEmit";
import {
  executionCandlesToVelaBars,
  executionCandleTimeToMillis,
  tesseraCandleTypeToVelaTimeframe,
  TESSERA_VELA_TIMEFRAMES,
  velaTimeframeToTesseraCandleType,
} from "../../../../../utils/vela/executionCandlesToVelaBars";
import {
  bumpTesseraNativeIndicators,
  ensureTesseraNativeIndicatorsRegistered,
  LANE_B_TO_NATIVE_TYPE,
  setTesseraNativeIndicatorsStoreKey,
  registerStrategyRdChipNatives,
  STRATEGY_RD_CHIP_REPLACED_TYPES,
  STRATEGY_RD_CHIP_TYPE_PREFIX,
  strategyRdChipNativeSpecs,
  TESSERA_CORE_NATIVE_TYPES,
  TESSERA_STRATEGY_RD_CORE_NATIVE_TYPES,
  type StrategyRdChipNativeSpec,
} from "../../../../../utils/vela/registerTesseraNativeIndicators";
import {
  hitTestPositionTool,
  pickTradeIdFromChartCrosshair,
} from "../../../../../utils/vela/tesseraTradeMarkerEmit";
import {
  getTesseraOverlayState,
  setTesseraOverlayState,
  tesseraOverlayStoreKey,
} from "../../../../../utils/vela/tesseraOverlayStore";
import {
  loadVelaDrawingsDocument,
  saveVelaDrawingsDocument,
} from "../../../../../utils/vela/velaDrawingsPersistence";
import {
  TRADE_REVIEW_CHART_BG_DARK,
  TRADE_REVIEW_CHART_BG_LIGHT,
  useTradeReviewThemeColors,
} from "./tradeReviewTheme";
import StrategyRdChartToolbar, {
  labActiveCell,
  labLibraryDialogOpen,
} from "./StrategyRdChartToolbar";
import "./executionCandlesVelaChrome.css";

export interface ExecutionCandlesChartProps {
  candles: ExecutionCandle[];
  trades: ExecutionTradeOverlay[];
  baselineTrades?: ExecutionTradeOverlay[];
  simulatedTrades?: Array<ExecutionTradeOverlay & { delta?: Record<string, unknown> }>;
  managementEvents?: Array<Record<string, unknown>>;
  /** TM overlay: lines only (no sim-exit / management-event dot markers). */
  simulationLineOverlay?: boolean;
  selectedTradeId: string | null;
  showPnlBackgroundOverlay?: boolean;
  /** Opt-in market-condition bgcolor (paired with `regimeOverlay`). */
  showRegimeOverlay?: boolean;
  regimeOverlay?: ExecutionCandlesRegimeOverlay | null;
  /** Lane B BE overlays bundle (include_overlays). */
  overlays?: ExecutionCandlesOverlaysBundle | null;
  /** Enabled Tessera Lane B overlay ids. */
  enabledLaneBOverlays?: readonly TesseraLaneBOverlayId[];
  /** Strategy Lab: echoed MACD/RSI/Stoch periods for FE-local pane series. */
  laneBOscParams?: StrategyRdOscComputeParams | null;
  /**
   * Strategy Lab: BE indicator-confluence series (`ohlc_stats.confluences.indicator_series`),
   * drawn over price and in sub-panels exactly as tested (§1.6c).
   */
  strategyRdIndicatorSeries?: Record<string, unknown> | null;
  /** Tessera candle_type for drawings persist + TF chips (`m`/`5m`/`h`/…). */
  candleType?: string;
  /** Allowed Tessera candle types for topbar TF chips (budget-gated). */
  allowedCandleTypes?: readonly string[];
  /** When set, TF chips are display-only (report/demo lock). */
  lockTimeframe?: boolean;
  /** When set, workspace symbol control cannot change the chart instrument. */
  lockInstrument?: boolean;
  /** Parent refetch when user picks a Vela TF chip. */
  onCandleTypeChange?: (candleType: string) => void;
  /** Parent refetch when user changes the workspace symbol control. */
  onInstrumentChange?: (symbol: string) => void;
  /** Compact chrome for inline Trade Log row. */
  denseChrome?: boolean;
  /** Candle body / border / wick colours; the Trade Review grey palette when omitted. */
  candleColors?: ExecutionCandleColors | null;
  focusTradeId?: string | null;
  onFocusTradeHandled?: () => void;
  onSelectTrade?: (tradeId: string | null) => void;
  /**
   * Strategy Lab: clickable match anchors (setup / path event times → match key).
   * Used when there are no Trade Review position tools to hit-test.
   */
  selectableHits?: Array<{ id: string; timeMs: number }> | null;
  /**
   * Strategy Lab: "Ask TES about this" on a candle or trade marker (right-click / long-press).
   * `tradeId` is set when the pointer was on a trade.
   */
  onAskAbout?: (target: { timeMs: number; tradeId: string | null }) => void;
  /**
   * Strategy Lab: IANA zone for the time axis / crosshair (Vela default is UTC). Drawings keep
   * their real UTC times — only the labels change.
   */
  displayTimeZone?: string | null;
  /**
   * Strategy Lab: our instruments in the chart's own symbol search, bars from the Lab. Registered
   * as a Vela data provider; the host's offline `data` still drives what's drawn.
   */
  symbolProvider?: StrategyRdChartSymbolProvider | null;
  height?: number;
  /** Shown on-chart (e.g. BTCUSDC). */
  instrumentLabel?: string;
  /** Strategy R&D: tint + label the pattern candle at this bar time. */
  highlightBarTime?: string | null;
  highlightDirection?: "long" | "short" | "neutral" | null;
  highlightLabel?: string | null;
  /** Strategy R&D Product B′ path markers (break / retest / entry). */
  pathHighlights?: Array<{
    barTime: string;
    direction?: "long" | "short" | "neutral" | null;
    label?: string | null;
    showText?: boolean;
    showArrow?: boolean;
    showWash?: boolean;
    emphasis?: "normal" | "strong" | "muted";
    kind?:
      | "setup"
      | "break"
      | "rejection"
      | "retest"
      | "location_armed"
      | "respect"
      | "reject"
      | "break_retest"
      | "entry"
      | "invalid"
      | "marker";
  }> | null;
  highlightLevels?: { entry: number; stop: number; target: number } | null;
  /** Strategy Lab: labeled setup / confluence drawings around the example bar. */
  conceptDrawings?: Array<{
    id: string;
    label: string;
    kind: "bar" | "zone" | "level" | "marker" | "ray";
    barTime?: string | null;
    fromBarTime?: string | null;
    toBarTime?: string | null;
    high?: number | null;
    low?: number | null;
    price?: number | null;
    fromPrice?: number | null;
    toPrice?: number | null;
    lineExtend?: "none" | "right";
    arrowRight?: boolean;
    showLabel?: boolean;
    markerStyle?: "arrowup" | "arrowdown" | "diamond";
    paneId?: string | null;
    /** Strategy Lab chart group (recipe chip): `setup`, `conf-N`, `entry`. */
    group?: string | null;
  }> | null;
  /**
   * Strategy Lab: one legend group per recipe chip (Setup / Confluence N / Entry / Volume).
   * When set, these replace the generic Concepts / Pattern / Trades / lane indicators.
   */
  strategyRdChips?: readonly StrategyRdChartChip[] | null;
  /**
   * Strategy Lab: one-shot pan/zoom onto these times when `frameGuideKey` changes.
   * Does not lock the viewport — the user can pan/zoom after.
   */
  frameAroundBarTimes?: readonly string[] | null;
  frameGuideKey?: string | null;
  /**
   * `full` fits the whole OHLC window (Lab preview with many hits).
   * `concept` zooms ~40 bars around `frameAroundBarTimes`. Default `concept`.
   */
  /** "trade": the selected trade from its setup to its exit, with a few candles either side. */
  frameFit?: "concept" | "full" | "trade";
  /** Exit Placement: legs replayed — omit sim SL/TP lines for legs not run. */
  simulatedLegs?: Array<"sl" | "tp">;
  /**
   * Workspace profile. `strategyRd` isolates persist/drawings/store from Trade Review
   * and seeds research natives (pattern / concepts / rule levels — not fills). Default `tradeReview`.
   */
  chartWorkspace?: "tradeReview" | "strategyRd";
  /**
   * Strategy Lab volume histogram. `true` mounts it (volume series + a volume
   * concept). `false` leaves it off so Indicators can add it. Omitted keeps the
   * Trade Review default (always on when bars carry a volume field).
   */
  mountVolumeHistogram?: boolean;
  /** Pan/zoom/fit toolbar (default on for Trade Review). */
  showNavigationControls?: boolean;
  visualStyle?: "default" | "reports";
}

type VelaChartApi = VelaWorkspace["chart"];

function mergeTradesForChart(
  trades: ExecutionTradeOverlay[],
  baselineTrades: ExecutionTradeOverlay[] | undefined,
  simulatedTrades: Array<ExecutionTradeOverlay & { delta?: Record<string, unknown> }> | undefined
): ExecutionTradeOverlay[] {
  const byId = new Map<string, ExecutionTradeOverlay>();
  for (const t of baselineTrades ?? trades) {
    if (t.trade_id) byId.set(t.trade_id, t);
  }
  for (const t of trades) {
    if (t.trade_id) byId.set(t.trade_id, { ...byId.get(t.trade_id), ...t });
  }
  for (const t of simulatedTrades ?? []) {
    if (!t.trade_id) continue;
    const prev = byId.get(t.trade_id);
    byId.set(t.trade_id, {
      ...(prev ?? t),
      ...t,
      simulation_overlay: t.simulation_overlay ?? "changed",
    });
  }
  return Array.from(byId.values());
}

function tesseraTypesToVelaTimeframes(types: readonly string[] | undefined): string[] {
  const src = types?.length ? types : ["m", "5m", "15m", "30m", "h", "4h"];
  const out: string[] = [];
  for (const t of src) {
    const v = tesseraCandleTypeToVelaTimeframe(t);
    if (!out.includes(v) && (TESSERA_VELA_TIMEFRAMES as readonly string[]).includes(v)) {
      out.push(v);
    }
  }
  return out.length ? out : [...TESSERA_VELA_TIMEFRAMES];
}

/** `ws.chart` / `ws.active` throw after destroy — never call them unguarded. */
function safeWorkspaceChart(ws: VelaWorkspace | null | undefined): VelaChartApi | null {
  if (!ws) return null;
  try {
    return ws.chart;
  } catch {
    return null;
  }
}

function ensureTesseraNativesOnChart(
  chart: VelaChartApi,
  enabledLaneB: ReadonlySet<TesseraLaneBOverlayId>,
  opts?: {
    core?: boolean;
    laneB?: boolean;
    /** Which core natives to seed (Trade Review vs Strategy Lab). */
    coreTypes?: readonly string[];
  }
): void {
  const seedCore = opts?.core !== false;
  const seedLaneB = opts?.laneB !== false;
  const coreTypes = opts?.coreTypes ?? TESSERA_CORE_NATIVE_TYPES;
  const present = new Set(chart.presentNativeIndicators());
  if (seedCore) {
    for (let i = 0; i < coreTypes.length; i += 1) {
      const type = coreTypes[i]!;
      if (!present.has(type)) {
        try {
          chart.addNativeIndicator(type);
        } catch {
          // ignore
        }
      }
    }
  }
  if (seedLaneB) {
    Array.from(enabledLaneB).forEach((id) => {
      const type = LANE_B_TO_NATIVE_TYPE[id];
      // Regime is core — don't double-manage via Lane B list.
      if (!type || type === "tessera-regime" || present.has(type)) return;
      try {
        chart.addNativeIndicator(type);
      } catch {
        // ignore
      }
    });
  }
}

const LAB_MANAGED_NATIVE_TYPES = new Set(Object.values(LANE_B_TO_NATIVE_TYPE));

/** Lab: add selected concept indicators (SMA, …) and drop ones that are no longer selected. */
function syncStrategyRdLaneBNatives(
  chart: VelaChartApi,
  enabledLaneB: ReadonlySet<TesseraLaneBOverlayId>
): void {
  const wanted = new Set<string>();
  Array.from(enabledLaneB).forEach((id) => {
    const type = LANE_B_TO_NATIVE_TYPE[id];
    if (type) wanted.add(type);
  });
  const present = new Set(chart.presentNativeIndicators());
  Array.from(wanted).forEach((type) => {
    if (present.has(type)) return;
    try {
      chart.addNativeIndicator(type);
    } catch {
      // ignore
    }
  });
  for (const handle of chart.indicators()) {
    const type = handle.nativeType;
    if (!type || !LAB_MANAGED_NATIVE_TYPES.has(type)) continue;
    if ((TESSERA_STRATEGY_RD_CORE_NATIVE_TYPES as readonly string[]).includes(type)) continue;
    if (wanted.has(type)) continue;
    try {
      handle.remove?.();
    } catch {
      // ignore
    }
  }
}

const NO_CORE_NATIVE_TYPES: readonly string[] = [];

/** Session memory of hidden Lab chip groups, by legend name (survives recipe changes). */
const strategyRdChipHidden = new Map<string, boolean>();

type ChipIndicatorHandle = {
  nativeType?: string;
  title?: string;
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
  remove?: () => void;
};

/**
 * Lab chip groups: register + mount one indicator per recipe chip (and its panels), drop the
 * generic Lab natives they replace and chips no longer in the recipe. Hidden groups stay hidden.
 */
function syncStrategyRdChipNatives(
  chart: VelaChartApi,
  specs: readonly StrategyRdChipNativeSpec[]
): void {
  const handles = chart.indicators() as unknown as ChipIndicatorHandle[];
  // Remember what the user hid before anything is re-mounted.
  for (const handle of handles) {
    if (!handle.nativeType?.startsWith(STRATEGY_RD_CHIP_TYPE_PREFIX) || !handle.title) continue;
    strategyRdChipHidden.set(handle.title, handle.visible === false);
  }
  const wanted = registerStrategyRdChipNatives(specs);
  const wantedSet = new Set(wanted);
  const replaced = new Set<string>(
    Array.from(LAB_MANAGED_NATIVE_TYPES).concat(STRATEGY_RD_CHIP_REPLACED_TYPES)
  );
  for (const handle of handles) {
    const type = handle.nativeType;
    if (!type) continue;
    const stale = type.startsWith(STRATEGY_RD_CHIP_TYPE_PREFIX) ? !wantedSet.has(type) : replaced.has(type);
    if (!stale) continue;
    try {
      handle.remove?.();
    } catch {
      // ignore
    }
  }
  const present = new Set(chart.presentNativeIndicators());
  for (const type of wanted) {
    if (present.has(type)) continue;
    try {
      chart.addNativeIndicator(type);
    } catch {
      // ignore
    }
  }
  const titleByType = new Map<string, string>();
  for (const spec of specs) {
    if (spec.mainType) titleByType.set(spec.mainType, spec.title);
    spec.panels.forEach((p) => titleByType.set(p.type, p.title));
  }
  for (const handle of chart.indicators() as unknown as ChipIndicatorHandle[]) {
    const title = handle.nativeType ? titleByType.get(handle.nativeType) : undefined;
    if (!title || !strategyRdChipHidden.get(title)) continue;
    try {
      handle.setVisible?.(false);
    } catch {
      // ignore
    }
  }
}

/** Lab: chip groups when the chart has them, else the per-lane natives. */
function syncStrategyRdLabNatives(
  chart: VelaChartApi,
  enabledLaneB: ReadonlySet<TesseraLaneBOverlayId>,
  chipSpecs: readonly StrategyRdChipNativeSpec[] | null
): void {
  if (chipSpecs) syncStrategyRdChipNatives(chart, chipSpecs);
  else syncStrategyRdLaneBNatives(chart, enabledLaneB);
}

function barsCarryVolume(
  bars: ReadonlyArray<{ volume?: number }>,
  requirePositive: boolean
): boolean {
  return bars.some((b) => {
    if (b.volume == null || !Number.isFinite(Number(b.volume))) return false;
    return requirePositive ? Number(b.volume) > 0 : true;
  });
}

/** Ensure Vela's built-in volume histogram is mounted when bars carry volume. */
function ensureVelaVolumeNative(
  chart: VelaChartApi,
  bars: ReadonlyArray<{ volume?: number }>,
  opts?: { requirePositive?: boolean }
): void {
  if (!barsCarryVolume(bars, opts?.requirePositive === true)) return;
  const present = new Set(chart.presentNativeIndicators());
  if (!present.has("volume")) {
    try {
      chart.addNativeIndicator("volume");
    } catch {
      // ignore
    }
  }
  // Equity share counts are 10^5–10^7; on the price pane they crush ~$100 candles
  // into a flat line at zero (Y-axis hundreds of thousands). Keep volume on its own pane.
  try {
    for (const handle of chart.indicators()) {
      if (handle.nativeType === "volume" && typeof handle.moveTo === "function") {
        handle.moveTo({ newPane: true });
        break;
      }
    }
  } catch {
    // ignore
  }
}

function removeVelaVolumeNative(chart: VelaChartApi): void {
  try {
    for (const handle of chart.indicators()) {
      if (handle.nativeType !== "volume") continue;
      handle.remove?.();
    }
  } catch {
    // ignore
  }
}

/**
 * Trade Review always mounts volume when the field exists.
 * Strategy Lab mounts only while `mode` is true, and only when that flag
 * changes — so the Indicators menu can add or remove Volume without the
 * next candle refresh putting it back.
 */
function syncVelaVolumeNative(
  chart: VelaChartApi,
  bars: ReadonlyArray<{ volume?: number }>,
  mode: boolean | undefined,
  intentRef: { current: boolean | null }
): void {
  if (mode == null) {
    ensureVelaVolumeNative(chart, bars);
    return;
  }
  if (intentRef.current === mode) return;
  intentRef.current = mode;
  if (mode) {
    ensureVelaVolumeNative(chart, bars, { requirePositive: true });
  } else {
    removeVelaVolumeNative(chart);
  }
}

function applyConceptVisibleRange(
  chart: VelaChartApi,
  bars: Array<{ time: number }>,
  focusTimesMs: readonly number[],
  fit: "concept" | "full" | "trade"
): boolean {
  const times = bars.map((b) => b.time);
  const range =
    fit === "full"
      ? strategyRdRecentVisibleRange(times)
      : fit === "trade"
        ? // Setup → exit fills the chart: a short trade is not lost in days of candles.
          strategyRdConceptVisibleRange(times, focusTimesMs, { padLeft: 12, padRight: 12, minBars: 40 })
        : strategyRdConceptVisibleRange(times, focusTimesMs);
  if (!range) return false;
  try {
    chart.setVisibleRange(range);
  } catch {
    return false;
  }
  try {
    // A dragged price axis stays frozen (manual scale): moving in time then leaves the
    // setup's candles above or below the view. Jumping to a setup refits price to them.
    chart.renderer?.set?.({ autoScale: true });
  } catch {
    // ignore
  }
  return true;
}

/** After natives mount, keep equity PnL on a study pane (not price candles). */
function ensureEquityPnlOnStudyPane(
  chart: VelaChartApi,
  placedRef: { current: boolean }
): void {
  if (placedRef.current) return;
  try {
    for (const handle of chart.indicators()) {
      if (handle.nativeType === "tessera-equity" && typeof handle.moveTo === "function") {
        handle.moveTo({ newPane: true });
        placedRef.current = true;
        break;
      }
    }
  } catch {
    // ignore
  }
}

function persistIdentity(symbol: string | undefined, candleType: string): string {
  return `${(symbol || "_").trim().toUpperCase()}|${candleType}`;
}

/** Match prior LWC Trade Review candle palette (grey, not green/red). */
const DEFAULT_CANDLE_UP = "#8E97A6";
const DEFAULT_CANDLE_DOWN = "#374151";

export type ExecutionCandleColors = {
  up: string;
  down: string;
  /** Wick colour for both directions; the body colours when omitted. */
  wick?: string;
  /** Body outlines; the body colours when omitted. */
  borderUp?: string;
  borderDown?: string;
};

function applyTesseraCandleColors(
  chart: VelaChartApi | null | undefined,
  colors?: ExecutionCandleColors | null
): void {
  if (!chart) return;
  const CANDLE_UP = colors?.up ?? DEFAULT_CANDLE_UP;
  const CANDLE_DOWN = colors?.down ?? DEFAULT_CANDLE_DOWN;
  try {
    chart.renderer?.set?.({
      upColor: CANDLE_UP,
      downColor: CANDLE_DOWN,
      candleVisible: true,
    });
  } catch {
    // ignore
  }
  const candlesCfg = {
    candles: {
      upColor: CANDLE_UP,
      downColor: CANDLE_DOWN,
      borderUpColor: colors?.borderUp ?? CANDLE_UP,
      borderDownColor: colors?.borderDown ?? CANDLE_DOWN,
      wickUpColor: colors?.wick ?? CANDLE_UP,
      wickDownColor: colors?.wick ?? CANDLE_DOWN,
      bodyVisible: true,
      borderVisible: true,
      wickVisible: true,
    },
  };
  try {
    chart.applyConfig?.(candlesCfg);
  } catch {
    // ignore
  }
  try {
    chart.renderer?.applyConfig?.(candlesCfg);
  } catch {
    // ignore
  }
}

function pushAxisTags(chart: VelaChartApi, tags: TesseraAxisTag[]): void {
  // The renderer's native-data channel: the public `chart.renderer` facade doesn't expose it,
  // the renderer behind `rendererControl` does (checked on @luxalgo/vela's native renderer).
  type Port = { setNativeData?: (type: string, data: unknown) => void };
  const c = chart as unknown as { renderer?: Port; rendererControl?: { renderer?: Port } };
  const port = c.rendererControl?.renderer?.setNativeData ? c.rendererControl.renderer : c.renderer;
  try {
    port?.setNativeData?.(TESSERA_AXIS_TAGS_LAYER, tags);
  } catch {
    // ignore — tags are a nicety
  }
}

/**
 * Prefer a recent window so Vela draws full candles (not sub-pixel aggregate sticks).
 * Clamp `to` to the last bar so we don't leave a blank right half past OHLC data.
 * Returns true when a range was applied.
 */
function frameRecentBarsIfNeeded(
  chart: VelaChartApi,
  bars: Array<{ time: number }>,
  opts?: { force?: boolean }
): boolean {
  if (bars.length < 100) return false;
  const visible = Math.min(360, Math.max(120, Math.floor(bars.length * 0.22)));
  if (!opts?.force && bars.length <= visible + 40) return false;
  const fromBar = bars[Math.max(0, bars.length - visible)]!;
  const toBar = bars[bars.length - 1]!;
  try {
    chart.setVisibleRange({ from: fromBar.time, to: toBar.time });
    return true;
  } catch {
    return false;
  }
}

/**
 * Trim a persisted / padded viewport that extends past real OHLC so Vela does not
 * extrapolate an empty right half (markers can still paint there via bar_time math).
 */
function clampVisibleRangeToBars(
  chart: VelaChartApi,
  bars: Array<{ time: number }>
): void {
  if (bars.length < 2) return;
  const first = bars[0]!.time;
  const last = bars[bars.length - 1]!.time;
  if (!(last > first)) return;
  let from: number;
  let to: number;
  try {
    const cur = chart.getVisibleRange?.() ?? null;
    if (!cur || !Number.isFinite(cur.from) || !Number.isFinite(cur.to) || !(cur.to > cur.from)) {
      return;
    }
    from = cur.from;
    to = cur.to;
  } catch {
    return;
  }
  let changed = false;
  if (to > last) {
    to = last;
    changed = true;
  }
  if (from < first) {
    from = first;
    changed = true;
  }
  if (!(to > from)) {
    from = first;
    to = last;
    changed = true;
  }
  if (!changed) return;
  try {
    chart.setVisibleRange({ from, to });
  } catch {
    // ignore
  }
}

export type StrategyRdChartSymbolProvider = {
  symbols: Array<{ ticker: string; description?: string; type?: string }>;
  /** `candleType` is the Tessera candle type (e.g. `15m`); `range` in epoch ms. */
  getBars: (
    ticker: string,
    candleType: string,
    range: { from?: number; to?: number; limit?: number }
  ) => Promise<OHLCV[]>;
};

/** Vela routes `TESSERA:XAUUSD` (and bare symbols it indexed) to this provider. */
const LAB_SYMBOL_PROVIDER = "TESSERA";

/** `TESSERA:XAUUSD` → `XAUUSD` (Vela reports picked symbols with the provider prefix). */
function stripProviderPrefix(symbol: string): string {
  const i = symbol.indexOf(":");
  return i >= 0 ? symbol.slice(i + 1) : symbol;
}

function registerSymbolProvider(
  chart: VelaChartApi,
  providerRef: React.MutableRefObject<StrategyRdChartSymbolProvider | null | undefined>
): void {
  const data = chart.data;
  if (!providerRef.current || !data?.registerProvider) return;
  try {
    data.unregisterProvider?.(LAB_SYMBOL_PROVIDER);
    void Promise.resolve(
      data.registerProvider(LAB_SYMBOL_PROVIDER, {
        getBars: (ticker: string, timeframe: string, range: { from?: number; to?: number; limit?: number }) =>
          providerRef.current
            ? providerRef.current.getBars(
                stripProviderPrefix(ticker),
                velaTimeframeToTesseraCandleType(timeframe),
                range ?? {}
              )
            : Promise.resolve([]),
        listSymbols: async () =>
          (providerRef.current?.symbols ?? []).map((s) => ({
            ticker: s.ticker,
            description: s.description,
            type: s.type,
          })),
      })
    ).catch(() => undefined);
  } catch {
    // older Vela without a data registry: the symbol control stays free text
  }
}

const ExecutionCandlesChart: React.FC<ExecutionCandlesChartProps> = ({
  candles,
  trades,
  baselineTrades,
  simulatedTrades,
  selectedTradeId,
  showPnlBackgroundOverlay = true,
  showRegimeOverlay = false,
  regimeOverlay = null,
  overlays = null,
  enabledLaneBOverlays,
  laneBOscParams = null,
  strategyRdIndicatorSeries = null,
  strategyRdChips = null,
  candleType = "h",
  allowedCandleTypes,
  lockTimeframe = false,
  lockInstrument = false,
  onCandleTypeChange,
  onInstrumentChange,
  denseChrome = false,
  candleColors = null,
  focusTradeId,
  onFocusTradeHandled,
  onSelectTrade,
  selectableHits = null,
  onAskAbout,
  displayTimeZone = null,
  symbolProvider = null,
  height = 520,
  instrumentLabel,
  visualStyle = "default",
  simulationLineOverlay = false,
  highlightBarTime = null,
  highlightDirection = null,
  highlightLabel = null,
  pathHighlights = null,
  highlightLevels = null,
  conceptDrawings = null,
  frameAroundBarTimes = null,
  frameGuideKey = null,
  frameFit = "concept",
  chartWorkspace = "tradeReview",
  mountVolumeHistogram,
}) => {
  const hostRef = useRef<HTMLDivElement>(null);
  /** "Full page" toolbar button: the chart fills the viewport (Esc / button closes). */
  const [fullPage, setFullPage] = useState(false);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const onToggle = () => setFullPage((prev) => !prev);
    host.addEventListener(TESSERA_VELA_FULL_PAGE_EVENT, onToggle);
    return () => host.removeEventListener(TESSERA_VELA_FULL_PAGE_EVENT, onToggle);
  }, []);
  useEffect(() => {
    if (!fullPage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullPage(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullPage]);
  const wsRef = useRef<VelaWorkspace | null>(null);
  /** Chart instance captured while the workspace is alive (survives destroy for persist). */
  const chartRef = useRef<VelaChartApi | null>(null);
  const aliveGenRef = useRef(0);
  const ignoreTfRef = useRef(false);
  const revisionRef = useRef(0);
  const mountedOnceRef = useRef(false);
  const didFrameRecentRef = useRef(false);
  const didPlaceEquityPaneRef = useRef(false);
  const didSeedCoreNativesRef = useRef(false);
  /** Last Strategy Lab volume intent applied. Null until the workspace syncs. */
  const volumeIntentRef = useRef<boolean | null>(null);
  const mountVolumeHistogramRef = useRef(mountVolumeHistogram);
  mountVolumeHistogramRef.current = mountVolumeHistogram;
  const framedGuideKeyRef = useRef<string | null>(null);
  const conceptFrameEpochRef = useRef(0);
  const conceptFrameUntilMsRef = useRef(0);

  // Latest values for event handlers without remounting Workspace.
  const barsRef = useRef(executionCandlesToVelaBars(candles));
  const enabledLaneBRef = useRef(new Set<TesseraLaneBOverlayId>());
  const candleTypeRef = useRef(candleType);
  const lockTimeframeRef = useRef(lockTimeframe);
  const lockInstrumentRef = useRef(lockInstrument);
  const instrumentLabelRef = useRef(instrumentLabel);
  const selectedTradeIdRef = useRef(selectedTradeId);
  const mergedTradesRef = useRef<ExecutionTradeOverlay[]>([]);
  const onAskAboutRef = useRef(onAskAbout);
  const symbolProviderRef = useRef(symbolProvider);
  symbolProviderRef.current = symbolProvider;
  const displayTimeZoneRef = useRef(displayTimeZone);
  displayTimeZoneRef.current = displayTimeZone;
  const [askMenu, setAskMenu] = useState<{
    x: number;
    y: number;
    timeMs: number;
    tradeId: string | null;
  } | null>(null);
  const onCandleTypeChangeRef = useRef(onCandleTypeChange);
  const onInstrumentChangeRef = useRef(onInstrumentChange);
  /** Strategy Lab: the top-bar slot the app's symbol / timeframe / indicator controls portal into. */
  const [labToolbarSlot, setLabToolbarSlot] = useState<{ el: HTMLElement; epoch: number } | null>(
    null
  );
  const labToolbarEpochRef = useRef(0);
  const [labSymbolSearchRequest, setLabSymbolSearchRequest] = useState<{
    query: string;
    seq: number;
  } | null>(null);
  const labTypingSeqRef = useRef(0);
  const onSelectTradeRef = useRef(onSelectTrade);
  const selectableHitsRef = useRef(selectableHits);
  /** TV position-tool stats: visible on hover, or pinned after click on the tool. */
  const showPositionStatsRef = useRef(false);
  const positionStatsPinnedRef = useRef(false);
  const storeKeyRef = useRef("");
  const persistDrawingsRef = useRef(true);
  const frameGuideKeyRef = useRef<string | null>(null);
  const frameFocusTimesMsRef = useRef<number[]>([]);
  const frameFitRef = useRef<"concept" | "full" | "trade">("concept");

  const { isDark } = useTradeReviewThemeColors(visualStyle);

  const bars = useMemo(() => executionCandlesToVelaBars(candles), [candles]);
  const barTimesMs = useMemo(() => zipBarTimesMs(candles), [candles]);
  const mergedTrades = useMemo(
    () => mergeTradesForChart(trades, baselineTrades, simulatedTrades),
    [trades, baselineTrades, simulatedTrades]
  );

  const rdSeries = useMemo(
    () => strategyRdChartSeriesFromResponse(strategyRdIndicatorSeries, barTimesMs),
    [barTimesMs, strategyRdIndicatorSeries]
  );

  const chipSpecsNext = useMemo(
    () => (strategyRdChips ? strategyRdChipNativeSpecs(strategyRdChips, rdSeries) : null),
    [rdSeries, strategyRdChips]
  );
  // Same chips → same array (plain data, compared by content), so a parent re-render
  // does not re-sync and recompute every chip indicator.
  const chipSpecsKey = useMemo(
    () => (chipSpecsNext ? JSON.stringify(chipSpecsNext) : null),
    [chipSpecsNext]
  );
  const chipSpecsStableRef = useRef({ specs: chipSpecsNext, key: chipSpecsKey });
  if (chipSpecsStableRef.current.key !== chipSpecsKey) {
    chipSpecsStableRef.current = { specs: chipSpecsNext, key: chipSpecsKey };
  }
  const chipSpecs = chipSpecsStableRef.current.specs;
  const chipSpecsRef = useRef(chipSpecs);
  chipSpecsRef.current = chipSpecs;

  const enabledLaneBNext = useMemo(() => {
    const set = new Set<TesseraLaneBOverlayId>(enabledLaneBOverlays ?? []);
    // BE series: one price overlay + one pane per sub series; drop FE lanes they duplicate.
    if (rdSeries.some((e) => e.pane === "price")) set.add("rd_price");
    rdSeries
      .filter((e) => e.pane !== "price")
      .forEach((_, i) => set.add(`rd_sub_${i}` as TesseraLaneBOverlayId));
    Array.from(strategyRdLanesCoveredBySeries(strategyRdIndicatorSeries)).forEach((lane) =>
      set.delete(lane as TesseraLaneBOverlayId)
    );
    // Regime is a core chart indicator — keep store data wired whenever the prop is on.
    if (showRegimeOverlay) set.add("regime");
    return set;
  }, [enabledLaneBOverlays, rdSeries, showRegimeOverlay, strategyRdIndicatorSeries]);
  // Same lanes → same Set: a parent re-render that only rebuilds its props must not
  // re-push the overlay store (every Tessera native recomputes over all bars).
  const enabledLaneBStableRef = useRef(enabledLaneBNext);
  const enabledLaneBPrev = enabledLaneBStableRef.current;
  if (
    enabledLaneBPrev !== enabledLaneBNext &&
    (enabledLaneBPrev.size !== enabledLaneBNext.size ||
      Array.from(enabledLaneBNext).some((id) => !enabledLaneBPrev.has(id)))
  ) {
    enabledLaneBStableRef.current = enabledLaneBNext;
  }
  const enabledLaneB = enabledLaneBStableRef.current;

  const normalizedOverlays = useMemo(
    () =>
      mergeComputedStrategyRdOverlays(
        candles,
        normalizeOverlaysBundle(candles, overlays, regimeOverlay),
        enabledLaneB,
        laneBOscParams
      ),
    [candles, overlays, regimeOverlay, enabledLaneB, laneBOscParams]
  );

  const frameFocusTimesMs = useMemo(
    () =>
      (frameAroundBarTimes ?? [])
        .map((t) => executionCandleTimeToMillis(t))
        .filter((t): t is number => t != null && Number.isFinite(t)),
    [frameAroundBarTimes]
  );

  const storeKey = useMemo(
    () => tesseraOverlayStoreKey(instrumentLabel || "_", candleType, chartWorkspace),
    [instrumentLabel, candleType, chartWorkspace]
  );

  // Lab chip groups replace the generic Concepts / Pattern / Trades natives.
  const useStrategyRdChips = strategyRdChips != null;
  const coreNativeTypes =
    chartWorkspace === "strategyRd"
      ? useStrategyRdChips
        ? NO_CORE_NATIVE_TYPES
        : TESSERA_STRATEGY_RD_CORE_NATIVE_TYPES
      : TESSERA_CORE_NATIVE_TYPES;
  const isStrategyRdWorkspace = chartWorkspace === "strategyRd";
  const candleColorsRef = useRef(candleColors);
  candleColorsRef.current = candleColors;

  const velaTf = tesseraCandleTypeToVelaTimeframe(candleType);
  const tfAllowlist = useMemo(
    () => tesseraTypesToVelaTimeframes(allowedCandleTypes),
    [allowedCandleTypes]
  );

  const chartPersistIdentity = useMemo(
    () => persistIdentity(instrumentLabel, candleType),
    [instrumentLabel, candleType]
  );

  const policyOverlayActive = useMemo(
    () =>
      mergedTrades.some(
        (t) => t.policy_overlay === "kept" || t.policy_overlay === "removed"
      ),
    [mergedTrades]
  );

  barsRef.current = bars;
  enabledLaneBRef.current = enabledLaneB;
  candleTypeRef.current = candleType;
  lockTimeframeRef.current = lockTimeframe;
  lockInstrumentRef.current = lockInstrument;
  instrumentLabelRef.current = instrumentLabel;
  selectedTradeIdRef.current = selectedTradeId;
  mergedTradesRef.current = mergedTrades;

  // The selected trade's entry and exit prices, tagged on the price axis (Strategy Lab).
  const axisTags = useMemo<TesseraAxisTag[]>(() => {
    if (!isStrategyRdWorkspace) return [];
    const id = selectedTradeId ?? focusTradeId ?? null;
    const trade = id != null ? mergedTrades.find((t) => t.trade_id === id) : undefined;
    return trade
      ? tradeAxisLevels(trade).map((l) => ({
          price: l.price,
          color: l.color,
          fromMs: l.timeMs ?? null,
          ...(l.key === "partial" ? { quiet: true, note: l.note ?? null } : {}),
        }))
      : [];
  }, [focusTradeId, isStrategyRdWorkspace, mergedTrades, selectedTradeId]);
  const axisTagsRef = useRef(axisTags);
  axisTagsRef.current = axisTags;
  useEffect(() => {
    const chart = chartRef.current;
    if (chart) pushAxisTags(chart, axisTags);
  }, [axisTags]);
  onCandleTypeChangeRef.current = onCandleTypeChange;
  onInstrumentChangeRef.current = onInstrumentChange;
  onSelectTradeRef.current = onSelectTrade;
  onAskAboutRef.current = onAskAbout;
  selectableHitsRef.current = selectableHits;
  storeKeyRef.current = storeKey;
  persistDrawingsRef.current = !isStrategyRdWorkspace;
  frameGuideKeyRef.current = frameGuideKey;
  frameFocusTimesMsRef.current = frameFocusTimesMs;
  frameFitRef.current = frameFit;

  const applyShowPositionStats = (next: boolean) => {
    if (showPositionStatsRef.current === next) return;
    showPositionStatsRef.current = next;
    const key = storeKeyRef.current;
    const prev = getTesseraOverlayState(key);
    if (!prev) return;
    revisionRef.current += 1;
    setTesseraOverlayState(key, {
      ...prev,
      showPositionStats: next,
      version: revisionRef.current,
    });
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (chart) bumpTesseraNativeIndicators(chart, revisionRef.current);
  };
  const applyShowPositionStatsRef = useRef(applyShowPositionStats);
  applyShowPositionStatsRef.current = applyShowPositionStats;

  const runConceptFrameTicks = (guideKey: string, epoch: number, ticks: number) => {
    const tick = (left: number) => {
      if (conceptFrameEpochRef.current !== epoch) return;
      if (frameGuideKeyRef.current !== guideKey) return;
      const live = chartRef.current ?? safeWorkspaceChart(wsRef.current);
      if (live) {
        applyConceptVisibleRange(
          live,
          barsRef.current,
          frameFocusTimesMsRef.current,
          frameFitRef.current
        );
      }
      if (left > 1) {
        requestAnimationFrame(() => tick(left - 1));
      }
    };
    tick(ticks);
  };

  /** One-shot pan/zoom onto the concept. Replays a few frames so Vela cannot overwrite it. */
  const beginConceptFrame = (guideKey: string | null) => {
    if (!guideKey) return false;
    if (framedGuideKeyRef.current === guideKey) return false;
    framedGuideKeyRef.current = guideKey;
    conceptFrameEpochRef.current += 1;
    conceptFrameUntilMsRef.current = Date.now() + 2500;
    runConceptFrameTicks(guideKey, conceptFrameEpochRef.current, 8);
    return true;
  };

  /** Re-apply the current locate if setMarket / persist raced the first apply. */
  const reinforceConceptFrame = () => {
    const guideKey = frameGuideKeyRef.current;
    if (!guideKey || framedGuideKeyRef.current !== guideKey) return false;
    if (Date.now() > conceptFrameUntilMsRef.current) return false;
    runConceptFrameTicks(guideKey, conceptFrameEpochRef.current, 6);
    return true;
  };

  // Clear TV stats pin when the selected trade changes.
  useEffect(() => {
    positionStatsPinnedRef.current = false;
    showPositionStatsRef.current = false;
  }, [selectedTradeId, focusTradeId]);

  // Push overlay store for Tessera native indicators.
  useEffect(() => {
    revisionRef.current += 1;
    setTesseraNativeIndicatorsStoreKey(storeKey);
    setTesseraOverlayState(storeKey, {
      barTimesMs,
      regime: showRegimeOverlay ? regimeOverlay : null,
      overlays: normalizedOverlays,
      enabledLaneB,
      trades: mergedTrades,
      selectedTradeId: selectedTradeId ?? focusTradeId ?? null,
      // PnL / regime visibility is owned by the chart Indicators panel.
      showPnlBackground: showPnlBackgroundOverlay,
      policyOverlayActive,
      simulationLineOverlay,
      showPositionStats: showPositionStatsRef.current,
      allPositionTools: isStrategyRdWorkspace && mergedTrades.length > 0,
      highlightBarTimeMs: executionCandleTimeToMillis(highlightBarTime),
      highlightDirection: highlightDirection ?? null,
      highlightLabel: highlightLabel ?? null,
      pathHighlights: (pathHighlights ?? []).flatMap((h) => {
        const timeMs = executionCandleTimeToMillis(h.barTime);
        if (timeMs == null || !Number.isFinite(timeMs)) return [];
        return [
          {
            timeMs,
            direction: h.direction ?? null,
            label: h.label ?? null,
            showText: h.showText !== false,
            showArrow: h.showArrow,
            showWash: h.showWash,
            emphasis: h.emphasis,
            kind: h.kind,
          },
        ];
      }),
      highlightLevels: highlightLevels ?? null,
      conceptDrawings: (conceptDrawings ?? [])
        .map((d) => {
          const timeMs = executionCandleTimeToMillis(d.barTime);
          const fromTimeMs = executionCandleTimeToMillis(d.fromBarTime);
          const toTimeMs = executionCandleTimeToMillis(d.toBarTime);
          return {
            id: d.id,
            label: d.label,
            kind: d.kind,
            timeMs: timeMs != null && Number.isFinite(timeMs) ? timeMs : null,
            fromTimeMs: fromTimeMs != null && Number.isFinite(fromTimeMs) ? fromTimeMs : null,
            toTimeMs: toTimeMs != null && Number.isFinite(toTimeMs) ? toTimeMs : null,
            high: d.high ?? null,
            low: d.low ?? null,
            // A pane marker moved onto price (its panel isn't there) keeps no pane-scale price.
            price:
              d.paneId && !strategyRdRetargetPaneId(d, rdSeries) ? null : d.price ?? null,
            fromPrice: d.fromPrice ?? null,
            toPrice: d.toPrice ?? null,
            lineExtend: d.lineExtend,
            arrowRight: d.arrowRight,
            showLabel: d.showLabel !== false,
            markerStyle: d.markerStyle,
            // MACD / ATR markers follow the BE panel that replaced their FE pane.
            paneId: strategyRdRetargetPaneId(d, rdSeries),
            sourcePaneId: d.paneId ?? null,
            group: d.group ?? null,
          };
        }),
      rdSeries,
      version: revisionRef.current,
    });
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (chart) {
      bumpTesseraNativeIndicators(chart, revisionRef.current);
    }
  }, [
    storeKey,
    barTimesMs,
    showRegimeOverlay,
    regimeOverlay,
    normalizedOverlays,
    enabledLaneB,
    mergedTrades,
    selectedTradeId,
    focusTradeId,
    showPnlBackgroundOverlay,
    policyOverlayActive,
    simulationLineOverlay,
    highlightBarTime,
    highlightDirection,
    highlightLabel,
    pathHighlights,
    highlightLevels,
    conceptDrawings,
    rdSeries,
  ]);

  // Focus trade → select once.
  useEffect(() => {
    if (!focusTradeId) return;
    onSelectTrade?.(focusTradeId);
    onFocusTradeHandled?.();
  }, [focusTradeId, onFocusTradeHandled, onSelectTrade]);

  // Mount workspace once per symbol+TF identity (not on every bar/theme tick).
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const gen = aliveGenRef.current + 1;
    aliveGenRef.current = gen;
    const isAlive = () => aliveGenRef.current === gen && wsRef.current != null;

    ensureTesseraNativeIndicatorsRegistered();
    setTesseraNativeIndicatorsStoreKey(storeKey);

    // Strict Mode remount: clear leftover DOM from a prior Workspace in this host.
    el.replaceChildren();

    // Strategy Lab must not restore Trade Review indicator layout / drawings.
    const persistKey = isStrategyRdWorkspace
      ? false
      : `tessera.tradeReview.vela.ws.v5:${(instrumentLabel || "_")
          .trim()
          .toUpperCase()}:${candleType}`;

    const savedDrawings = isStrategyRdWorkspace
      ? null
      : loadVelaDrawingsDocument(instrumentLabel || "", candleType);

    // Strategy Lab: symbol / timeframe / indicators are the app's own controls
    // (StrategyRdChartToolbar, portaled into the top bar), so the library's are dropped.
    const nativeMarketPicker = Boolean(symbolProviderRef.current);
    const topbarLeft = nativeMarketPicker
      ? denseChrome
        ? (["undo-redo"] as const)
        : (["undo-redo", "style"] as const)
      : denseChrome
        ? (["indicators", "undo-redo"] as const)
        : lockTimeframe
          ? (["symbol", "indicators", "undo-redo", "style"] as const)
          : (["symbol", "timeframes", "indicators", "undo-redo", "style"] as const);

    ignoreTfRef.current = true;
    let ws: VelaWorkspace;
    try {
      ws = new VelaWorkspace(el, {
        layout: false,
        symbol: instrumentLabel || "TESSERA",
        timeframe: velaTf,
        data: barsRef.current,
        volume: isStrategyRdWorkspace ? mountVolumeHistogram === true : true,
        live: false,
        theme: isDark ? "dark" : "light",
        upColor: candleColorsRef.current?.up ?? DEFAULT_CANDLE_UP,
        downColor: candleColorsRef.current?.down ?? DEFAULT_CANDLE_DOWN,
        autofocus: false,
        drawingToolbar: true,
        // Bottom chips switch TF + fetch — hide until we can drive viewport-only.
        bottombar: false,
        watermark: true,
        statusline: true,
        timeframes: tfAllowlist,
        topbar: {
          left: [...topbarLeft],
          right: denseChrome ? ["panels"] : ["panels", "screenshot"],
        },
        persist: persistKey,
      });
    } catch (err) {
      console.error("[ExecutionCandlesVelaChart] Failed to create VelaWorkspace", err);
      return;
    }

    wsRef.current = ws;
    if (displayTimeZoneRef.current) {
      try {
        ws.setTimezone(displayTimeZoneRef.current);
      } catch {
        // unknown zone — keep UTC
      }
    }
    const chart = safeWorkspaceChart(ws);
    if (!chart) {
      try {
        ws.destroy();
      } catch {
        // ignore
      }
      wsRef.current = null;
      return;
    }
    chartRef.current = chart;
    mountedOnceRef.current = true;
    pushAxisTags(chart, axisTagsRef.current);
    registerSymbolProvider(chart, symbolProviderRef);

    // Strategy Lab: a slot at the head of the library top bar for the app's controls.
    let labToolbarEl: HTMLElement | null = null;
    if (nativeMarketPicker) {
      const topbarEl = ws.root.querySelector<HTMLElement>(".vela-widget-topbar");
      if (topbarEl) {
        labToolbarEl = el.ownerDocument.createElement("span");
        labToolbarEl.className = "tessera-vela-lab-toolbar";
        labToolbarEl.dataset.tesseraVelaLabToolbar = "";
        topbarEl.prepend(labToolbarEl);
        labToolbarEpochRef.current += 1;
        setLabSymbolSearchRequest(null);
        setLabToolbarSlot({ el: labToolbarEl, epoch: labToolbarEpochRef.current });
      }
    }
    // Bare letters on the chart open the app's symbol search (the library's picker
    // would otherwise catch them). Library dialogs keep their own typing.
    const onLabTyping = (ev: KeyboardEvent) => {
      if (!labToolbarEl || !isAlive()) return;
      if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
      if (!/^[a-zA-Z]$/.test(ev.key)) return;
      const target = ev.target as HTMLElement | null;
      if (!target || !ws.root.contains(target)) return;
      const tag = (target.tagName ?? "").toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target.isContentEditable ||
        target.getAttribute?.("role") === "textbox"
      ) {
        return;
      }
      if (labLibraryDialogOpen(ws)) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (lockInstrumentRef.current) return;
      labTypingSeqRef.current += 1;
      setLabSymbolSearchRequest({ query: ev.key.toUpperCase(), seq: labTypingSeqRef.current });
    };
    if (labToolbarEl) el.addEventListener("keydown", onLabTyping, true);

    applyTesseraCandleColors(chart, candleColorsRef.current);

    if (savedDrawings) {
      try {
        chart.drawings.fromJSON(savedDrawings);
      } catch {
        // ignore corrupt local drawings
      }
    }

    ensureTesseraNativesOnChart(chart, enabledLaneBRef.current, {
      core: true,
      laneB: !isStrategyRdWorkspace,
      coreTypes: coreNativeTypes,
    });
    if (isStrategyRdWorkspace) {
      syncStrategyRdLabNatives(chart, enabledLaneBRef.current, chipSpecsRef.current);
    }
    volumeIntentRef.current = null;
    syncVelaVolumeNative(
      chart,
      barsRef.current,
      isStrategyRdWorkspace ? mountVolumeHistogramRef.current : undefined,
      volumeIntentRef
    );
    didSeedCoreNativesRef.current = true;
    bumpTesseraNativeIndicators(chart, revisionRef.current);
    // Persist may restore style + a padded viewport past OHLC — re-assert greys,
    // theme tokens, and clamp.
    requestAnimationFrame(() => {
      if (!isAlive()) return;
      try {
        ws.setTheme(isDark ? "dark" : "light");
      } catch {
        // ignore
      }
      const live = chartRef.current ?? safeWorkspaceChart(wsRef.current);
      applyTesseraCandleColors(live, candleColorsRef.current);
      if (!live) return;
      if (!isStrategyRdWorkspace) {
        ensureEquityPnlOnStudyPane(live, didPlaceEquityPaneRef);
      }
      clampVisibleRangeToBars(live, barsRef.current);
      if (isStrategyRdWorkspace) {
        if (beginConceptFrame(frameGuideKeyRef.current)) {
          didFrameRecentRef.current = true;
        } else if (reinforceConceptFrame()) {
          didFrameRecentRef.current = true;
        } else if (!didFrameRecentRef.current && applyConceptVisibleRange(live, barsRef.current, [], "full")) {
          // No setup to focus yet: open on readable recent candles, not the whole window.
          didFrameRecentRef.current = true;
        }
      } else if (!didFrameRecentRef.current && !selectedTradeIdRef.current) {
        if (frameRecentBarsIfNeeded(live, barsRef.current, { force: true })) {
          didFrameRecentRef.current = true;
        }
      }
    });

    const unsubMarket = chart.on(
      "market:changed",
      (payload: { timeframe: string; symbol?: string }) => {
        if (!isAlive() || ignoreTfRef.current) return;
        if (lockTimeframeRef.current || lockInstrumentRef.current) {
          const nextSym = typeof payload.symbol === "string" ? payload.symbol.trim() : "";
          const curSym = (instrumentLabelRef.current || "").trim();
          const nextTessera = velaTimeframeToTesseraCandleType(payload.timeframe);
          const tfDrift =
            lockTimeframeRef.current && nextTessera !== candleTypeRef.current;
          const symDrift =
            lockInstrumentRef.current &&
            Boolean(nextSym) &&
            nextSym.toUpperCase() !== curSym.toUpperCase();
          if (!tfDrift && !symDrift) return;
          ignoreTfRef.current = true;
          const liveChart = safeWorkspaceChart(wsRef.current);
          if (!liveChart) {
            ignoreTfRef.current = false;
            return;
          }
          void liveChart
            .setMarket({
              data: barsRef.current,
              timeframe: tesseraCandleTypeToVelaTimeframe(candleTypeRef.current),
              symbol: instrumentLabelRef.current || "TESSERA",
            })
            .finally(() => {
              if (isAlive()) ignoreTfRef.current = false;
            });
          return;
        }

        const nextSym =
          typeof payload.symbol === "string" ? stripProviderPrefix(payload.symbol.trim()) : "";
        const curSym = stripProviderPrefix((instrumentLabelRef.current || "").trim());
        if (
          nextSym &&
          nextSym.toUpperCase() !== curSym.toUpperCase()
        ) {
          onInstrumentChangeRef.current?.(nextSym);
        }

        const nextTessera = velaTimeframeToTesseraCandleType(payload.timeframe);
        if (nextTessera === candleTypeRef.current) return;
        try {
          const liveChart = safeWorkspaceChart(wsRef.current);
          if (liveChart && persistDrawingsRef.current) {
            saveVelaDrawingsDocument(
              instrumentLabelRef.current || "",
              candleTypeRef.current,
              liveChart.drawings.toJSON()
            );
          }
        } catch {
          // ignore
        }
        onCandleTypeChangeRef.current?.(nextTessera);
      }
    );

    const unsubState = ws.on("state:changed", () => {
      if (!isAlive()) return;
      try {
        const liveChart = safeWorkspaceChart(wsRef.current);
        if (liveChart && persistDrawingsRef.current) {
          saveVelaDrawingsDocument(
            instrumentLabelRef.current || "",
            candleTypeRef.current,
            liveChart.drawings.toJSON()
          );
        }
      } catch {
        // ignore
      }
    });

    const readCrosshair = (): { time: number | null; price: number | null } => {
      try {
        return {
          time: ws.active?.lastCrossTime ?? null,
          price: ws.active?.lastCrossPrice ?? null,
        };
      } catch {
        return { time: null, price: null };
      }
    };

    const resolveSelectedTrade = () => {
      const id = selectedTradeIdRef.current;
      if (!id) return null;
      return mergedTradesRef.current.find((t) => t.trade_id === id) ?? null;
    };

    const onDomClick = () => {
      if (!isAlive()) return;
      const { time: crossTime, price: crossPrice } = readCrosshair();
      const select = onSelectTradeRef.current;
      if (crossTime == null) return;

      const overlayState = getTesseraOverlayState(storeKeyRef.current);
      if (overlayState?.allPositionTools && crossPrice != null) {
        for (const t of mergedTradesRef.current) {
          if (!t.trade_id) continue;
          if (!hitTestPositionTool(t, barsRef.current, crossTime, crossPrice)) continue;
          if (t.trade_id === selectedTradeIdRef.current) {
            positionStatsPinnedRef.current = true;
            applyShowPositionStatsRef.current(true);
            return;
          }
          positionStatsPinnedRef.current = false;
          applyShowPositionStatsRef.current(false);
          select?.(t.trade_id);
          return;
        }
      }

      const selected = resolveSelectedTrade();
      if (
        selected &&
        hitTestPositionTool(selected, barsRef.current, crossTime, crossPrice)
      ) {
        positionStatsPinnedRef.current = true;
        applyShowPositionStatsRef.current(true);
        return;
      }

      positionStatsPinnedRef.current = false;
      applyShowPositionStatsRef.current(false);

      if (!select) return;
      const picked = pickTradeIdFromChartCrosshair(
        mergedTradesRef.current,
        barsRef.current,
        crossTime,
        crossPrice
      );
      if (picked) {
        const cur = selectedTradeIdRef.current;
        select(picked === cur ? null : picked);
        return;
      }

      const hits = selectableHitsRef.current;
      if (hits?.length) {
        let best: { id: string; dist: number } | null = null;
        // ~3 bars on 5m; still tight enough on 15m.
        const toleranceMs = 15 * 60 * 1000;
        for (const hit of hits) {
          const dist = Math.abs(hit.timeMs - crossTime);
          if (dist > toleranceMs) continue;
          if (!best || dist < best.dist) best = { id: hit.id, dist };
        }
        if (best) {
          select(best.id);
          return;
        }
      }
    };

    const onDomMove = () => {
      if (!isAlive()) return;
      const selected = resolveSelectedTrade();
      if (!selected) {
        if (showPositionStatsRef.current) applyShowPositionStatsRef.current(false);
        return;
      }
      const { time: crossTime, price: crossPrice } = readCrosshair();
      if (crossTime == null) return;
      const over = hitTestPositionTool(
        selected,
        barsRef.current,
        crossTime,
        crossPrice
      );
      applyShowPositionStatsRef.current(over || positionStatsPinnedRef.current);
    };

    const onDomLeave = () => {
      if (!isAlive()) return;
      if (!positionStatsPinnedRef.current) {
        applyShowPositionStatsRef.current(false);
      }
    };

    // "Ask TES about this": right-click, or a long press on touch.
    const openAskMenu = (clientX: number, clientY: number): boolean => {
      if (!isAlive() || !onAskAboutRef.current) return false;
      const { time, price } = readCrosshair();
      if (time == null) return false;
      const tradeId = pickTradeIdFromChartCrosshair(mergedTradesRef.current, barsRef.current, time, price);
      setAskMenu({ x: clientX, y: clientY, timeMs: time, tradeId: tradeId ?? null });
      return true;
    };
    const onContextMenu = (e: MouseEvent) => {
      if (openAskMenu(e.clientX, e.clientY)) e.preventDefault();
    };
    let pressTimer: number | null = null;
    let pressStart: { x: number; y: number } | null = null;
    const cancelPress = () => {
      if (pressTimer != null) window.clearTimeout(pressTimer);
      pressTimer = null;
      pressStart = null;
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "touch") return;
      cancelPress();
      pressStart = { x: e.clientX, y: e.clientY };
      const { clientX, clientY } = e;
      pressTimer = window.setTimeout(() => {
        pressTimer = null;
        openAskMenu(clientX, clientY);
      }, 550);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (pressStart && Math.hypot(e.clientX - pressStart.x, e.clientY - pressStart.y) > 10) cancelPress();
    };
    el.addEventListener("click", onDomClick);
    el.addEventListener("mousemove", onDomMove);
    el.addEventListener("mouseleave", onDomLeave);
    el.addEventListener("contextmenu", onContextMenu);
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", cancelPress);
    el.addEventListener("pointercancel", cancelPress);

    const raf = requestAnimationFrame(() => {
      if (!isAlive()) return;
      ignoreTfRef.current = false;
      try {
        ws.resize();
      } catch {
        // ignore destroyed
      }
    });

    return () => {
      aliveGenRef.current = gen + 1; // invalidate in-flight callbacks for this gen
      cancelAnimationFrame(raf);
      el.removeEventListener("click", onDomClick);
      el.removeEventListener("mousemove", onDomMove);
      el.removeEventListener("mouseleave", onDomLeave);
      el.removeEventListener("contextmenu", onContextMenu);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", cancelPress);
      el.removeEventListener("pointercancel", cancelPress);
      el.removeEventListener("keydown", onLabTyping, true);
      if (labToolbarEl) {
        setLabToolbarSlot((prev) => (prev?.el === labToolbarEl ? null : prev));
      }
      cancelPress();
      try {
        unsubMarket();
      } catch {
        // ignore
      }
      try {
        unsubState();
      } catch {
        // ignore
      }
      try {
        const snapChart = chartRef.current;
        if (snapChart && persistDrawingsRef.current) {
          saveVelaDrawingsDocument(
            instrumentLabelRef.current || "",
            candleTypeRef.current,
            snapChart.drawings.toJSON()
          );
        }
      } catch {
        // ignore
      }
      chartRef.current = null;
      wsRef.current = null;
      try {
        ws.destroy();
      } catch {
        // ignore
      }
      el.replaceChildren();
    };
  }, [chartPersistIdentity, storeKey, denseChrome, lockTimeframe, chartWorkspace, coreNativeTypes, isStrategyRdWorkspace]);

  // Reset framing when the chart identity remounts.
  useEffect(() => {
    didFrameRecentRef.current = false;
    didPlaceEquityPaneRef.current = false;
    didSeedCoreNativesRef.current = false;
    framedGuideKeyRef.current = null;
    conceptFrameEpochRef.current += 1;
    conceptFrameUntilMsRef.current = 0;
  }, [chartPersistIdentity]);

  // Feed new bars without full remount when only candles change.
  useEffect(() => {
    const ws = wsRef.current;
    const chart = chartRef.current ?? safeWorkspaceChart(ws);
    if (!ws || !chart || !mountedOnceRef.current) return;

    const genAtStart = aliveGenRef.current;
    ignoreTfRef.current = true;
    void chart
      .setMarket({
        symbol: instrumentLabel || "TESSERA",
        timeframe: velaTf,
        data: bars,
      })
      .then(() => {
        if (aliveGenRef.current !== genAtStart) return;
        const live = chartRef.current ?? safeWorkspaceChart(wsRef.current);
        if (!live) return;
        // Core natives seed once so the Indicators panel can remove them
        // without us re-adding on every candle refresh.
        ensureTesseraNativesOnChart(live, enabledLaneBRef.current, {
          core: !didSeedCoreNativesRef.current,
          laneB: !isStrategyRdWorkspace,
          coreTypes: coreNativeTypes,
        });
        if (isStrategyRdWorkspace) {
          syncStrategyRdLabNatives(live, enabledLaneBRef.current, chipSpecsRef.current);
        }
        syncVelaVolumeNative(
          live,
          barsRef.current,
          isStrategyRdWorkspace ? mountVolumeHistogramRef.current : undefined,
          volumeIntentRef
        );
        didSeedCoreNativesRef.current = true;
        bumpTesseraNativeIndicators(live, revisionRef.current);
        applyTesseraCandleColors(live, candleColorsRef.current);
        if (!isStrategyRdWorkspace) {
          ensureEquityPnlOnStudyPane(live, didPlaceEquityPaneRef);
        }
        clampVisibleRangeToBars(live, bars);
        if (isStrategyRdWorkspace) {
          if (beginConceptFrame(frameGuideKeyRef.current)) {
            didFrameRecentRef.current = true;
          } else if (reinforceConceptFrame()) {
            didFrameRecentRef.current = true;
          } else if (!didFrameRecentRef.current && applyConceptVisibleRange(live, barsRef.current, [], "full")) {
            // No setup to focus yet: open on readable recent candles, not the whole window.
            didFrameRecentRef.current = true;
          }
        } else if (!didFrameRecentRef.current && !selectedTradeIdRef.current) {
          if (frameRecentBarsIfNeeded(live, bars, { force: true })) {
            didFrameRecentRef.current = true;
          }
        }
      })
      .catch(() => {
        // ignore aborted/destroyed setMarket
      })
      .finally(() => {
        if (aliveGenRef.current === genAtStart) {
          ignoreTfRef.current = false;
        }
      });
  }, [bars, velaTf, instrumentLabel, coreNativeTypes, isStrategyRdWorkspace]);

  useEffect(() => {
    if (!isStrategyRdWorkspace) return;
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (!chart || !mountedOnceRef.current) return;
    syncVelaVolumeNative(chart, barsRef.current, mountVolumeHistogram, volumeIntentRef);
  }, [isStrategyRdWorkspace, mountVolumeHistogram]);

  useEffect(() => {
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (!chart || !mountedOnceRef.current) return;
    if (isStrategyRdWorkspace) {
      syncStrategyRdLabNatives(chart, enabledLaneB, chipSpecs);
    } else {
      ensureTesseraNativesOnChart(chart, enabledLaneB, { core: false, laneB: true });
    }
    bumpTesseraNativeIndicators(chart, revisionRef.current);
  }, [chipSpecs, enabledLaneB, isStrategyRdWorkspace]);

  useEffect(() => {
    if (!isStrategyRdWorkspace) return;
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (!chart || !mountedOnceRef.current) return;
    if (beginConceptFrame(frameGuideKey)) {
      didFrameRecentRef.current = true;
    }
  }, [frameGuideKey, frameFocusTimesMs, frameFit, isStrategyRdWorkspace]);

  // Theme follow (no remount). Re-apply grey candles — setTheme resets palette greens/reds.
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    try {
      ws.setTheme(isDark ? "dark" : "light");
    } catch {
      // ignore destroyed
    }
    applyTesseraCandleColors(chartRef.current ?? safeWorkspaceChart(ws), candleColorsRef.current);
  }, [isDark]);

  // Candle colour setting (no remount).
  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    applyTesseraCandleColors(chartRef.current ?? safeWorkspaceChart(ws), candleColors);
  }, [candleColors?.up, candleColors?.down, candleColors?.wick, candleColors?.borderUp, candleColors?.borderDown]);

  // Frame only on external focus (list click). Chart selection must not auto-zoom —
  // the trade was already clicked in view and jumping the viewport is confusing.
  useEffect(() => {
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (!chart) return;
    const tradeId = focusTradeId;
    if (!tradeId) return;
    const trade = mergedTrades.find((t) => t.trade_id === tradeId);
    if (!trade?.entry_time) return;
    const from = Date.parse(trade.entry_time);
    if (!Number.isFinite(from)) return;
    const exitRaw =
      trade.simulation_overlay === "changed" && trade.sim_exit_time
        ? trade.sim_exit_time
        : trade.exit_time;
    const toRaw = exitRaw ? Date.parse(exitRaw) : NaN;
    const to = Number.isFinite(toRaw) ? toRaw : from;
    const span = Math.max(to - from, 60 * 60 * 1000);
    const pad = Math.max(span * 0.75, 4 * 60 * 60 * 1000);
    const bars = barsRef.current;
    const dataFrom = bars[0]?.time;
    const dataTo = bars[bars.length - 1]?.time;
    let rangeFrom = from - pad;
    let rangeTo = to + pad;
    if (dataFrom != null) rangeFrom = Math.max(rangeFrom, dataFrom);
    if (dataTo != null) rangeTo = Math.min(rangeTo, dataTo);
    if (!(rangeTo > rangeFrom)) return;
    try {
      chart.setVisibleRange({ from: rangeFrom, to: rangeTo });
    } catch {
      // ignore if chart not ready
    }
  }, [focusTradeId, mergedTrades]);

  // Resize.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const ws = wsRef.current;
      if (!ws) return;
      try {
        ws.resize();
      } catch {
        // ignore destroyed
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const bg = isDark ? TRADE_REVIEW_CHART_BG_DARK : TRADE_REVIEW_CHART_BG_LIGHT;
  const velaTheme = isDark ? "dark" : "light";

  // Keep host data-attr in sync for pane-chrome CSS (Vela's pill is theme-blind).
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    el.dataset.tesseraVelaTheme = velaTheme;
  }, [velaTheme]);

  const getLabWorkspace = useCallback(() => wsRef.current, []);

  // Strategy Lab toolbar: the same active-cell calls the library's pickers make, so the
  // `market:changed` handler above (locks, drawings save, parent refetch) runs unchanged.
  const pickLabSymbol = useCallback((ticker: string) => {
    if (lockInstrumentRef.current) return;
    const next = stripProviderPrefix(ticker.trim());
    if (!next) return;
    const cur = stripProviderPrefix((instrumentLabelRef.current || "").trim());
    if (next.toUpperCase() === cur.toUpperCase()) return;
    const cell = labActiveCell(wsRef.current);
    if (!cell) return;
    try {
      // The library picker reports `PROVIDER:TICKER` for provider-listed symbols.
      cell.setSymbol(`${LAB_SYMBOL_PROVIDER}:${next}`);
    } catch {
      // destroyed
    }
  }, []);

  const pickLabTimeframe = useCallback((tf: string) => {
    if (lockTimeframeRef.current) return;
    const cell = labActiveCell(wsRef.current);
    if (!cell) return;
    try {
      cell.setTimeframe(tf);
    } catch {
      // destroyed
    }
  }, []);

  const labToolbarNode =
    labToolbarSlot && symbolProvider
      ? createPortal(
          <StrategyRdChartToolbar
            key={labToolbarSlot.epoch}
            getWorkspace={getLabWorkspace}
            symbol={stripProviderPrefix((instrumentLabel || "").trim())}
            timeframe={velaTf}
            timeframes={tfAllowlist}
            showTimeframe={!lockTimeframe}
            symbolLocked={lockInstrument}
            symbols={symbolProvider.symbols}
            onPickSymbol={pickLabSymbol}
            onPickTimeframe={pickLabTimeframe}
            symbolSearchRequest={labSymbolSearchRequest}
          />,
          labToolbarSlot.el
        )
      : null;

  // The instrument list usually arrives after the chart: re-index the symbol search.
  const symbolListKey = symbolProvider ? symbolProvider.symbols.map((s) => s.ticker).join(",") : "";
  useEffect(() => {
    const chart = chartRef.current ?? safeWorkspaceChart(wsRef.current);
    if (!chart || !symbolListKey) return;
    registerSymbolProvider(chart, symbolProviderRef);
  }, [symbolListKey]);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws || !displayTimeZone) return;
    try {
      ws.setTimezone(displayTimeZone);
    } catch {
      // unknown zone — keep the current one
    }
  }, [displayTimeZone]);

  useEffect(() => {
    if (!askMenu) return;
    const close = () => setAskMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
    };
  }, [askMenu]);

  const askMenuNode =
    askMenu && typeof document !== "undefined"
      ? createPortal(
          <div
            role="menu"
            data-tessera-chart-ask-menu=""
            className="fixed z-[1500] min-w-[11rem] rounded-[6px] border border-border bg-popover p-1 text-sm text-popover-foreground shadow-md"
            style={{ left: askMenu.x, top: askMenu.y }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              autoFocus
              className="w-full rounded-[4px] px-2 py-1.5 text-left hover:bg-accent focus:bg-accent focus:outline-none"
              onClick={() => {
                onAskAboutRef.current?.({ timeMs: askMenu.timeMs, tradeId: askMenu.tradeId });
                setAskMenu(null);
              }}
            >
              {askMenu.tradeId ? "Ask TES about this trade" : "Ask TES about this"}
            </button>
          </div>,
          document.body
        )
      : null;

  return (
    <>
    {askMenuNode}
    {labToolbarNode}
    <Box
      ref={hostRef}
      className="tessera-vela-host"
      w={fullPage ? "100vw" : "100%"}
      h={fullPage ? "100vh" : `${height}px`}
      minH={fullPage ? "100vh" : `${height}px`}
      bg={bg}
      borderRadius={fullPage || isStrategyRdWorkspace ? "0" : "md"}
      overflow="hidden"
      position={fullPage ? "fixed" : "relative"}
      inset={fullPage ? 0 : undefined}
      zIndex={fullPage ? 1400 : undefined}
      // Thin edge so the chart reads as its own card next to panels like Matches. The Lab draws
      // its own lines between columns, so its chart sits square and edge-to-edge.
      border={fullPage || isStrategyRdWorkspace ? "none" : "1px solid var(--border)"}
      data-full-page={fullPage ? "" : undefined}
      data-trade-review-vela-chart=""
      data-tessera-vela-theme={velaTheme}
      data-candle-type={candleType}
      sx={{
        // Keep Vela attribution mark visible (Apache NOTICE) — do not hide.
        "& .vela-attribution, & [data-vela-attribution]": {
          opacity: 1,
        },
      }}
    />
    </>
  );
};

export default ExecutionCandlesChart;
